/* ── Focus Fire Reticle ──────────────────────────────────────────
 * Pulsing red ring billboarded to the camera, marking the enemy
 * drone currently auto-targeted by FocusFire. The server publishes
 * the chosen target id on `player.focusTargetId`; this renderer
 * looks it up against the interpolated drones each frame.
 *
 * Public API matches the project's renderer convention:
 *   - constructor adds the ring to the scene
 *   - update(deltaMs, target) animates and repositions
 *   - dispose() cleans up
 * ───────────────────────────────────────────────────────────── */

import * as THREE from 'three/webgpu';

interface Vec3 {
  x: number;
  y: number;
  z: number;
}

export interface FocusFireTarget {
  position: Vec3;
}

const RING_INNER_RADIUS = 1.4;
const RING_OUTER_RADIUS = 2.0;
const PULSE_SPEED = 4.0;
const PULSE_SCALE_MIN = 1.0;
const PULSE_SCALE_MAX = 1.4;
const RING_COLOR = 0xff2a2a;

export class FocusFireReticle {
  readonly mesh: THREE.Mesh;
  private readonly material: THREE.MeshBasicMaterial;
  private readonly geometry: THREE.RingGeometry;
  private elapsed = 0;

  constructor(scene: THREE.Object3D) {
    // Flat ring geometry — billboarded each frame via lookAt(camera).
    this.geometry = new THREE.RingGeometry(RING_INNER_RADIUS, RING_OUTER_RADIUS, 48);
    this.material = new THREE.MeshBasicMaterial({
      color: RING_COLOR,
      transparent: true,
      opacity: 0.85,
      side: THREE.DoubleSide,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      toneMapped: false,
    });
    this.mesh = new THREE.Mesh(this.geometry, this.material);
    this.mesh.frustumCulled = false;
    this.mesh.visible = false;
    this.mesh.renderOrder = 9;
    scene.add(this.mesh);
  }

  /**
   * Update the reticle for the current frame. Pass the resolved
   * focus target (or null when FocusFire is inactive / no target).
   * The renderer billboards toward `camera` for a stable on-screen ring.
   */
  update(deltaMs: number, target: FocusFireTarget | null, camera: THREE.Camera): void {
    if (!target) {
      if (this.mesh.visible) this.mesh.visible = false;
      return;
    }
    this.elapsed += deltaMs / 1000;

    const wave = (Math.sin(this.elapsed * PULSE_SPEED) + 1) * 0.5;
    const scale = PULSE_SCALE_MIN + (PULSE_SCALE_MAX - PULSE_SCALE_MIN) * wave;

    this.mesh.position.set(target.position.x, target.position.y, target.position.z);
    this.mesh.scale.setScalar(scale);
    this.mesh.lookAt(camera.position);
    this.mesh.visible = true;
  }

  dispose(): void {
    if (this.mesh.parent) this.mesh.parent.remove(this.mesh);
    this.geometry.dispose();
    this.material.dispose();
  }
}
